import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Send } from 'lucide-react';
import { getPartner } from '../../services/api';
import PartnerProgramLabel from './PartnerProgramLabel';
import { normalizeProgram } from '../../utils/partnersAdmin';

const STAGE_DEFAULTS = {
  trial: 'En prueba',
  paying: 'Pagando',
  cancelled: 'Cancelado',
  clawed_back: 'Clawback',
  lost: 'Perdido',
};

const PAYOUT_STATUS_DEFAULTS = {
  payable: 'A pagar',
  rollover: 'Arrastra',
  approved: 'Aprobado',
  paid: 'Pagado',
};

const TAX_DEFAULTS = {
  monotributo: 'Monotributo',
  responsable_inscripto: 'Responsable inscripto',
};

/**
 * Detalle de un código (RES-90 / RES-93 / RES-98): datos del código, escalón CM, próximo
 * pago, conversiones con etapa y comisión, historial de payouts y datos de cobro.
 */
function PartnerDetailModal({
  partner: initialPartner,
  onClose,
  onEdit,
  onInvite,
  fmt,
  landingUrl,
  onCopyLink,
}) {
  const { t } = useTranslation();
  const [partner, setPartner] = useState(initialPartner);
  const [metrics, setMetrics] = useState(initialPartner.metrics || {});
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPartner(initialPartner.code)
      .then((response) => {
        if (cancelled || !response) return;
        if (response.partner) setPartner((prev) => ({ ...prev, ...response.partner }));
        if (response.metrics) setMetrics((prev) => ({ ...prev, ...response.metrics }));
      })
      .catch((error) => console.error('Error loading partner detail:', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialPartner.code]);

  const program = normalizeProgram(partner.program);
  const isCm = program === 'cm';
  const conversions = Array.isArray(metrics.conversions) ? metrics.conversions : [];
  const payouts = Array.isArray(metrics.payouts) ? metrics.payouts : [];
  const nextPayout = metrics.nextPayout || null;
  const payoutDetails = partner.payoutDetails || null;

  const stageLabel = (stage) => t(`partners.stage.${stage}`, STAGE_DEFAULTS[stage] || stage);
  const payoutStatusLabel = (status) =>
    t(`partners.payoutStatus.${status}`, PAYOUT_STATUS_DEFAULTS[status] || status);

  const handleInvite = async () => {
    setInviting(true);
    try {
      const result = await onInvite(partner);
      if (result) setPartner((prev) => ({ ...prev, ...result }));
    } finally {
      setInviting(false);
    }
  };

  const conversionDate = (c) => {
    if (c.stage === 'trial') return c.attributedAt;
    if (c.stage === 'cancelled' || c.stage === 'clawed_back' || c.stage === 'lost') {
      return c.cancelledAt || c.firstPaidAt || c.attributedAt;
    }
    return c.firstPaidAt || c.attributedAt;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-content--wide partners-detail-modal">
        <div className="modal-header">
          <h2>
            {partner.partnerName} · <code className="partner-code">{partner.code}</code>
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.close', 'Cerrar')}
          >
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div>
              <span className="detail-label">{t('partners.statusLabel', 'Estado')}</span>
              <span className={`status-badge status-${partner.status}`}>
                {t(`partners.status.${partner.status}`, partner.status)}
              </span>
            </div>
            <div>
              <span className="detail-label">{t('partners.program.label', 'Programa')}</span>
              <PartnerProgramLabel program={program} cmTier={metrics.cmTier} />
            </div>
            <div>
              <span className="detail-label">{t('partners.tier', 'Tier')}</span>
              <span className="tier-badge">{partner.tier}</span>
            </div>
            <div>
              <span className="detail-label">{t('partners.email', 'Email')}</span>
              <span>{partner.partnerEmail}</span>
            </div>
            <div>
              <span className="detail-label">{t('partners.commissionPct', 'Comisión %')}</span>
              {isCm ? (
                <span>
                  {metrics.cmTier ? `${metrics.cmTier.pct}%` : '—'} ·{' '}
                  {t('partners.program.cmNoCap', 'sin tope de meses')}
                </span>
              ) : (
                <span>
                  {partner.commissionPct ?? '—'}% × {partner.commissionMonths ?? '—'}{' '}
                  {t('partners.months', 'meses')}
                </span>
              )}
            </div>
            {!isCm && (
              <div>
                <span className="detail-label">{t('partners.signOnBonus', 'Sign-on bonus')}</span>
                <span>
                  {fmt.money(partner.signOnBonus)} ({t('partners.signOnUsed', 'usados')}:{' '}
                  {partner.signOnUsed ?? 0}/3)
                </span>
              </div>
            )}
            <div>
              <span className="detail-label">
                {t('partners.enterpriseFlat', 'Flat Enterprise (ARS)')}
              </span>
              <span>{fmt.money(partner.enterpriseFlat)}</span>
            </div>
            <div>
              <span className="detail-label">
                {t('partners.contractStart', 'Inicio de contrato')}
              </span>
              <span>{fmt.date(partner.contractStart)}</span>
            </div>
            <div>
              <span className="detail-label">{t('partners.contractEnd', 'Fin de contrato')}</span>
              <span>{fmt.date(partner.contractEnd)}</span>
            </div>
            <div>
              <span className="detail-label">
                {t('partners.portalInvite.label', 'Portal del partner')}
              </span>
              {partner.portalUserId ? (
                <span className="portal-badge" data-testid="portal-active">
                  {t('partners.portalInvite.active', 'Portal activo')}
                </span>
              ) : partner.status === 'terminated' ? (
                <span className="text-muted">
                  {t('partners.portalInvite.notInvited', 'Sin invitar')}
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  data-testid="detail-invite-portal"
                  disabled={inviting}
                  onClick={handleInvite}
                >
                  <Send size={14} /> {t('partners.portalInvite.button', 'Invitar al portal')}
                </button>
              )}
            </div>
          </div>

          <div className="detail-link">
            <span className="detail-label">{t('partners.landingLink', 'Link de la landing')}</span>
            <div className="detail-link-row">
              <code>{landingUrl}</code>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onCopyLink}>
                <Copy size={14} /> {t('partners.copyLink', 'Copiar link')}
              </button>
            </div>
          </div>

          <div className="metrics-cards" data-testid="detail-metrics">
            <div className="metric-card">
              <span className="detail-label">
                {t('partners.activeConversions', 'Conversiones')}
              </span>
              <strong>{metrics.activeConversions ?? 0}</strong>
              {metrics.trials > 0 && (
                <span className="metric-sub">
                  {t('partners.metrics.trials', '+{{count}} en prueba', { count: metrics.trials })}
                </span>
              )}
            </div>
            <div className="metric-card">
              <span className="detail-label">{t('partners.totalMRR', 'MRR')}</span>
              <strong>{fmt.money(metrics.mrr ?? 0)}</strong>
            </div>
            <div className="metric-card">
              <span className="detail-label">
                {t('partners.metrics.commissionLifetime', 'Comisión de toda la vida')}
              </span>
              <strong>{fmt.money(metrics.commissionAccrued ?? 0)}</strong>
              <span className="metric-sub">
                {t('partners.metrics.paid', 'Pagado: {{amount}}', {
                  amount: fmt.money(metrics.commissionPaid ?? 0),
                })}
              </span>
            </div>
            <div className="metric-card" data-testid="next-payout">
              <span className="detail-label">
                {t('partners.metrics.nextPayout', 'Próximo pago')}
              </span>
              {nextPayout ? (
                <>
                  <strong>{fmt.money(nextPayout.amount)}</strong>
                  <span className="metric-sub">
                    {fmt.period(nextPayout.period)} · {payoutStatusLabel(nextPayout.status)}
                    {nextPayout.estimatedDate ? ` · ${fmt.date(nextPayout.estimatedDate)}` : ''}
                  </span>
                </>
              ) : (
                <span className="metric-sub">
                  {t('partners.metrics.noNextPayout', 'Sin pago pendiente')}
                </span>
              )}
              {metrics.pendingRollover > 0 && (
                <span className="metric-sub">
                  {t('partners.metrics.pendingRollover', 'Arrastre acumulado: {{amount}}', {
                    amount: fmt.money(metrics.pendingRollover),
                  })}
                </span>
              )}
            </div>
          </div>

          <h3 className="detail-section-title">
            {t('partners.conversionsTitle', 'Conversiones y comisiones')}
          </h3>
          {loading && conversions.length === 0 ? (
            <p className="detail-pending">{t('common.loading', 'Cargando…')}</p>
          ) : conversions.length === 0 ? (
            <p className="detail-pending" data-testid="no-conversions">
              {t('partners.metrics.noConversions', 'Sin conversiones todavía')}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="partners-table partners-subtable" data-testid="conversions-table">
                <thead>
                  <tr>
                    <th>{t('partners.metrics.location', 'Local')}</th>
                    <th>{t('partners.conversionStage', 'Etapa')}</th>
                    <th>{t('partners.conversionDate', 'Fecha')}</th>
                    <th>{t('partners.totalMRR', 'MRR')}</th>
                    <th>{t('partners.metrics.commissionToDate', 'Comisión a la fecha')}</th>
                    <th>{t('partners.metrics.commissionUntil', 'Comisiona hasta')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions.map((c) => (
                    <tr key={c.subscriptionId || c.organizationId}>
                      <td data-label={t('partners.metrics.location', 'Local')}>
                        <span>{c.organizationName || c.organizationId}</span>
                        <span className="cell-sub">
                          {c.planId}
                          {c.billingMode === 'resale'
                            ? ` · ${t('partners.metrics.resale', 'Reventa')}`
                            : ''}
                        </span>
                      </td>
                      <td data-label={t('partners.conversionStage', 'Etapa')}>
                        <span className={`stage-badge stage-${c.stage}`}>
                          {stageLabel(c.stage)}
                        </span>
                      </td>
                      <td data-label={t('partners.conversionDate', 'Fecha')}>
                        {fmt.date(conversionDate(c))}
                      </td>
                      <td data-label={t('partners.totalMRR', 'MRR')}>{fmt.money(c.mrr)}</td>
                      <td
                        data-label={t('partners.metrics.commissionToDate', 'Comisión a la fecha')}
                      >
                        {fmt.money(c.commissionToDate)}
                      </td>
                      <td data-label={t('partners.metrics.commissionUntil', 'Comisiona hasta')}>
                        {c.commissionEndsPeriod
                          ? fmt.period(c.commissionEndsPeriod)
                          : t('partners.metrics.noCap', 'Sin tope')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="detail-section-title">
            {t('partners.metrics.payoutsTitle', 'Historial de pagos')}
          </h3>
          {payouts.length === 0 ? (
            <p className="detail-pending" data-testid="no-payouts">
              {t('partners.metrics.noPayouts', 'Todavía no se calculó ningún período')}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="partners-table partners-subtable" data-testid="payouts-table">
                <thead>
                  <tr>
                    <th>{t('partners.metrics.period', 'Período')}</th>
                    <th>{t('partners.metrics.recurring', 'Comisión')}</th>
                    <th>{t('partners.metrics.bonus', 'Bono')}</th>
                    <th>{t('partners.metrics.rolloverIn', 'Arrastre previo')}</th>
                    <th>{t('partners.metrics.payable', 'A pagar')}</th>
                    <th>{t('partners.metrics.rolloverOut', 'Pasa al mes siguiente')}</th>
                    <th>{t('partners.statusLabel', 'Estado')}</th>
                    <th>{t('partners.conversionDate', 'Fecha')}</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.period}>
                      <td data-label={t('partners.metrics.period', 'Período')}>
                        {fmt.period(p.period)}
                      </td>
                      <td data-label={t('partners.metrics.recurring', 'Comisión')}>
                        {fmt.money(p.recurringCommission)}
                        <span className="cell-sub">
                          {p.commissionPct}%
                          {p.program === 'cm' && p.activeLocations !== undefined
                            ? ` · ${t('partners.metrics.activeLocations', '{{count}} locales', {
                                count: p.activeLocations,
                              })}`
                            : ''}
                        </span>
                      </td>
                      <td data-label={t('partners.metrics.bonus', 'Bono')}>
                        {fmt.money(p.signOnBonus)}
                      </td>
                      <td data-label={t('partners.metrics.rolloverIn', 'Arrastre previo')}>
                        {fmt.money(p.rolloverIn)}
                      </td>
                      <td data-label={t('partners.metrics.payable', 'A pagar')}>
                        <strong>{fmt.money(p.payableAmount)}</strong>
                      </td>
                      <td data-label={t('partners.metrics.rolloverOut', 'Pasa al mes siguiente')}>
                        {fmt.money(p.rolloverOut)}
                      </td>
                      <td data-label={t('partners.statusLabel', 'Estado')}>
                        <span className={`payout-badge payout-${p.status}`}>
                          {payoutStatusLabel(p.status)}
                        </span>
                      </td>
                      <td data-label={t('partners.conversionDate', 'Fecha')}>
                        {fmt.date(p.paidAt || p.estimatedDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="detail-section-title">
            {t('partners.payoutDetails.title', 'Datos de cobro')}
          </h3>
          {payoutDetails ? (
            <div className="detail-grid" data-testid="payout-details">
              <div>
                <span className="detail-label">
                  {t('partners.payoutDetails.holderName', 'Titular')}
                </span>
                <span>{payoutDetails.holderName || '—'}</span>
              </div>
              <div>
                <span className="detail-label">{t('partners.payoutDetails.cuit', 'CUIT')}</span>
                <span>{payoutDetails.cuit || '—'}</span>
              </div>
              <div>
                <span className="detail-label">
                  {t('partners.payoutDetails.cbuOrAlias', 'CBU o alias')}
                </span>
                <span className="mono">{payoutDetails.cbuOrAlias || '—'}</span>
              </div>
              <div>
                <span className="detail-label">
                  {t('partners.payoutDetails.taxCondition', 'Condición fiscal')}
                </span>
                <span>
                  {payoutDetails.taxCondition
                    ? t(
                        `partners.payoutDetails.tax.${payoutDetails.taxCondition}`,
                        TAX_DEFAULTS[payoutDetails.taxCondition] || payoutDetails.taxCondition
                      )
                    : '—'}
                  {payoutDetails.invoiceType
                    ? ` · ${t('partners.payoutDetails.invoice', 'Factura {{type}}', {
                        type: payoutDetails.invoiceType,
                      })}`
                    : ''}
                </span>
              </div>
              <div>
                <span className="detail-label">
                  {t('partners.payoutDetails.updatedAt', 'Actualizado')}
                </span>
                <span>{fmt.date(payoutDetails.updatedAt)}</span>
              </div>
            </div>
          ) : (
            <p className="detail-pending" data-testid="no-payout-details">
              {t(
                'partners.payoutDetails.empty',
                'El partner todavía no cargó sus datos de cobro en el portal.'
              )}
            </p>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.close', 'Cerrar')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => onEdit(partner)}>
            {t('common.edit', 'Editar')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PartnerDetailModal;
